import React, { useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FormNavigationProvider, NavTextField, NavSelect } from '../components/FormNavigation';
import { Add, NavigateNext, Search } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { showNotification } from '../store/notificationSlice';
import { useAppDispatch, useAppSelector } from '../store';
import { apiService } from '../services/api';
import type { LedgerPage, LedgerEntry } from '../types/leave';

const getEntryDateDisplay = (entry: LedgerEntry): string => {
  if (entry.transactionType === 'CONSUMPTION' && entry.startDate && entry.endDate) {
    return entry.startDate === entry.endDate ? entry.startDate : `${entry.startDate} to ${entry.endDate}`;
  }
  if (entry.postedAt) {
    return entry.postedAt;
  }
  if (entry.startDate && entry.endDate) {
    return entry.startDate === entry.endDate ? entry.startDate : `${entry.startDate} to ${entry.endDate}`;
  }
  if (entry.startDate) {
    return entry.startDate;
  }
  if (entry.createdAt) {
    return entry.createdAt.slice(0, 10);
  }
  return '—';
};

const getEntrySortKey = (entry: LedgerEntry): string => {
  return entry.startDate || entry.postedAt || (entry.createdAt ? entry.createdAt.slice(0, 10) : '') || '';
};

const LeaveTransactions: React.FC = () => {
  const dispatch = useAppDispatch();
  const leaveTypes = useAppSelector((s) => s.leaveTypes.leaveTypes).filter((t) => t.isActive);

  const [employeeId, setEmployeeId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [page, setPage] = useState<LedgerPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const filterContainerRef = React.useRef<HTMLElement | null>(null);

  const sortedEntries = React.useMemo(() => {
    if (!page?.entries) return [];
    return [...page.entries].sort((a, b) => {
      const dateA = getEntrySortKey(a);
      const dateB = getEntrySortKey(b);
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB); // Oldest first (chronological order)
      }
      return (a.createdAt || '').localeCompare(b.createdAt || '');
    });
  }, [page?.entries]);

  if (leaveTypes.length > 0 && !selectedTypeId) {
    setSelectedTypeId(leaveTypes[0].id);
  }

  const fetchTransactions = async () => {
    if (!employeeId.trim() || !selectedTypeId) return;
    setLoading(true);
    setPage(null);
    try {
      const res = await apiService.listLeaveTransactions(employeeId.trim(), selectedTypeId);
      setPage(res.data);
    } catch (err: any) {
      dispatch(showNotification({ message: err.message ?? 'Failed to fetch transactions', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const selectedTypeName = leaveTypes.find((t) => t.id === selectedTypeId)?.name ?? '';

  return (
    <DashboardLayout>
      <Box>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Leave Management</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>Transactions</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Leave Transactions</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setPostOpen(true)}
            disabled={!employeeId.trim() || !selectedTypeId}
          >
            Post Transaction
          </Button>
        </Box>

        <FormNavigationProvider containerRef={filterContainerRef}>
          <Box ref={filterContainerRef} sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
            <NavTextField
              label="Employee ID"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              size="small"
              sx={{ minWidth: 280 }}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchTransactions(); }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Leave Type</InputLabel>
              <NavSelect label="Leave Type" value={selectedTypeId} onChange={(e) => setSelectedTypeId(e.target.value as string)}>
                {leaveTypes.map((lt) => (
                  <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>
                ))}
              </NavSelect>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <Search />}
              onClick={fetchTransactions}
              disabled={loading || !employeeId.trim() || !selectedTypeId}
            >
              Load
            </Button>
          </Box>
        </FormNavigationProvider>

        {page && (
          <>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedTypeName} balance for <strong>{employeeId}</strong>:
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color={page.balance >= 0 ? 'success.main' : 'error.main'}
              >
                {page.balance}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEntries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        No transactions found.
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedEntries.map((entry, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {getEntryDateDisplay(entry)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.transactionType}
                          size="small"
                          color={
                            entry.transactionType === 'ACCRUAL' ? 'success'
                            : entry.transactionType === 'CONSUMPTION' || entry.transactionType === 'LAPSE' ? 'error'
                            : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={entry.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {entry.amount >= 0 ? '+' : ''}{entry.amount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{entry.note || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {postOpen && (
          <PostTransactionDialog
            open
            employeeId={employeeId}
            leaveTypeId={selectedTypeId}
            leaveTypeName={selectedTypeName}
            onClose={() => setPostOpen(false)}
            onPosted={() => {
              setPostOpen(false);
              dispatch(showNotification({ message: 'Transaction posted', severity: 'success' }));
              fetchTransactions();
            }}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

// ─── Post Transaction Dialog ────────────────────────────────────────────────

interface PostTransactionDialogProps {
  open: boolean;
  employeeId: string;
  leaveTypeId: string;
  leaveTypeName: string;
  onClose: () => void;
  onPosted: () => void;
}

const PostTransactionDialog: React.FC<PostTransactionDialogProps> = ({
  open, employeeId, leaveTypeId, leaveTypeName, onClose, onPosted,
}) => {
  const dispatch = useAppDispatch();
  const [txType, setTxType] = useState<'CONSUMPTION' | 'ADJUSTMENT'>('CONSUMPTION');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navContainerRef = React.useRef<HTMLElement | null>(null);

  const handleSubmit = async () => {
    const valErrs: Record<string, string> = {};
    if (txType === 'CONSUMPTION') {
      if (!startDate) valErrs['startDate'] = 'Start date is required.';
      if (!endDate) valErrs['endDate'] = 'End date is required.';
      if (startDate && endDate && endDate < startDate) valErrs['endDate'] = 'End date must be on or after start date.';
    } else {
      if (!amount || isNaN(Number(amount)) || Number(amount) === 0) {
        valErrs['amount'] = 'Amount must be a non-zero number.';
      }
    }
    if (Object.keys(valErrs).length > 0) {
      setErrors(valErrs);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      if (txType === 'CONSUMPTION') {
        await apiService.postLeaveConsumption({
          employeeId,
          leaveTypeId,
          transactionType: 'CONSUMPTION',
          startDate,
          endDate,
          note: note.trim() || undefined,
        });
      } else {
        await apiService.postLeaveAdjustment({
          employeeId,
          leaveTypeId,
          transactionType: 'ADJUSTMENT',
          amount: Number(amount),
          note: note.trim() || undefined,
        });
      }
      onPosted();
    } catch (err: any) {
      if (err?.jsonMessage) {
        setErrors(err.jsonMessage);
      } else {
        dispatch(showNotification({ message: err?.message ?? 'Failed to post transaction', severity: 'error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ ref: navContainerRef }}
    >
      <FormNavigationProvider containerRef={navContainerRef}>
        <DialogTitle>Post Leave Transaction</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Employee: <strong>{employeeId}</strong> · Leave Type: <strong>{leaveTypeName}</strong>
            </Typography>

            <FormControl size="small" fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <NavSelect
                label="Transaction Type"
                value={txType}
                onChange={(e) => { setTxType(e.target.value as 'CONSUMPTION' | 'ADJUSTMENT'); setErrors({}); }}
              >
                <MenuItem value="CONSUMPTION">Consumption (debit)</MenuItem>
                <MenuItem value="ADJUSTMENT">Adjustment (credit/debit)</MenuItem>
              </NavSelect>
            </FormControl>

            {txType === 'CONSUMPTION' ? (
              <>
                <NavTextField
                  label="Start Date"
                  type="date"
                  navFieldType="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  error={!!errors['startDate']}
                  helperText={errors['startDate']}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <NavTextField
                  label="End Date"
                  type="date"
                  navFieldType="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  fullWidth
                  error={!!errors['endDate']}
                  helperText={errors['endDate']}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </>
            ) : (
              <NavTextField
                label="Amount"
                type="number"
                navFieldType="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                size="small"
                fullWidth
                helperText={errors['amount'] || 'Positive to add, negative to deduct'}
                error={!!errors['amount']}
              />
            )}

            {errors['employeeId'] && (
              <Alert severity="error">{errors['employeeId']}</Alert>
            )}

            <NavTextField
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}>
            Post
          </Button>
        </DialogActions>
      </FormNavigationProvider>
    </Dialog>
  );
};

export default LeaveTransactions;
